import "./index.scss";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, InputRef, Popconfirm, Table, TablePaginationConfig } from "antd";
import { useSelector } from "react-redux";
import { renderNullableDate, renderNullableText } from "../../../utils/renderNullableText";
import store, { RootState } from "../../../store";
import { useActions } from "../../../hooks/useActions";
import { I18n, Translate } from "react-redux-i18n";
import { AttributeWithValueType, ContactType } from "../../../types/contactType";
import { DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { deleteContact, setCurrentContact } from "../../../store/slices/contacts/contactsService";
import { ContactsAttributeType } from "../../../types/ContactsAttributeType";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { GetContactsParams } from "../../../types/contactsParams";

const renderActions = (contact: ContactType) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <Button type="text" onClick={() => store.dispatch(setCurrentContact(contact))}>
      <EditOutlined />
    </Button>
    <Popconfirm
      title={I18n.t("contactDeleteConfirmation")}
      onConfirm={() => {
        if (contact?.contactId) {
          store.dispatch(deleteContact(contact.contactId));
        }
      }}
    >
      <Button type="text">
        <DeleteOutlined />
      </Button>
    </Popconfirm>
  </div>
);

const useColumnSearch = (handleSearch: (value: string, propertyKey: string) => void) => {
  const searchInput = useRef<InputRef>(null);

  return (dataIndex: string): ColumnType<ContactType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8, width: 250 }} onKeyDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            ref={searchInput}
            placeholder={I18n.t("enterTheData")}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onFocus={() => setTimeout(() => searchInput.current?.select(), 100)}
            onPressEnter={() => {
              confirm();
              handleSearch(selectedKeys[0] as string, dataIndex);
            }}
            style={{ marginBottom: 8, flexGrow: 1 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              handleSearch(selectedKeys[0] as string, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ flex: 1 }}
          >
            <Translate value={"search"} />
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              handleSearch("", dataIndex);
              confirm();
            }}
            size="small"
            style={{ flex: 1 }}
          >
            <Translate value={"reset"} />
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
  });
};

const ContactsTableComponent: FC = () => {
  const { list, attributesList } = useSelector((state: RootState) => ({
    list: state.contacts.list.map((it) => ({ ...it, ...toAttributesObj(it.properties) })),
    attributesList: state.contacts.attributesList,
  }));

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState<{ propertyKey?: string; value?: string }>({});
  const { filterContacts, getAttributes } = useActions();
  const totalElements = useSelector((state: RootState) => state.contacts.totalElements);

  const handleSearch = useCallback((value: string, propertyKey: string) => {
    setFilters({ propertyKey, value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleTableChange = useCallback((paginationInfo: TablePaginationConfig) => {
    setPagination({
      current: paginationInfo.current ?? 1,
      pageSize: paginationInfo.pageSize ?? 10,
    });
  }, []);

  const getColumnSearchProps = useColumnSearch(handleSearch);

  const columns = useCallback(
    (attributes: Array<ContactsAttributeType>): ColumnsType<ContactType> => [
      {
        title: I18n.t("fullName"),
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 200,
        render: renderNullableText,
      },
      {
        title: I18n.t("updateAt"),
        dataIndex: "updatedAt",
        key: "updatedAt",
        fixed: "left",
        width: 200,
        render: renderNullableDate,
      },
      ...attributes.map((it) => ({
        dataIndex: it.propertyKey,
        title: it.description,
        key: it.propertyKey,
        width: 200,
        render: renderNullableText,
        ...getColumnSearchProps(it.propertyKey),
      })),
      {
        title: I18n.t("actions"),
        key: "actions",
        fixed: "right",
        width: 120,
        render: renderActions,
      },
    ],
    [getColumnSearchProps],
  );

  useEffect(() => {
    const params: GetContactsParams = {
      pageNumber: pagination.current - 1,
      pageSize: pagination.pageSize,
      paged: true,
      unpaged: false,
    };

    if (filters.propertyKey) {
      params.propertyKey = filters.propertyKey;
      params.value = filters.value;
    }

    filterContacts(params);
  }, [pagination, filters]);

  useEffect(() => {
    getAttributes();
  }, []);

  return (
    <Table
      dataSource={list}
      scroll={{ x: 1000 }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        showSizeChanger: true,
        showQuickJumper: false,
        size: "small",
        total: totalElements,
      }}
      onChange={handleTableChange}
      columns={columns(attributesList)}
      rowKey={(record) => record?.contactId + ""}
    />
  );
};

export default ContactsTableComponent;

const toAttributesObj = (attr: Array<AttributeWithValueType>): any => {
  return attr.reduce((obj, item) => ({ ...obj, [item.propertyKey]: item.value }), {});
};
